import {type PaymentStatusPayload, assertNever} from "@mytour/shared";
import {useQueryClient} from "@tanstack/react-query";
import {useState} from "react";

import {keysStore as platformUserKeysStore, useAccount} from "@mytour/platform";
import {subscriptionKeysStore} from "../query/subscriptionQueries";
import {EndOfTrialModal} from "./modals/EndOfTrialModal";
import {FailedRecurringPaymentModal} from "./modals/FailedRecurringPaymentModal";
import {FailedSmsPaymentModal} from "./modals/FailedSmsPaymentModal";
import {PaymentDataModal} from "./modals/PaymentDataModal";
import {RevolutModal} from "./modals/RevolutModal";

type Props = {
  paymentStatus: PaymentStatusPayload;
  onDismiss: () => void;
};

export function PaymentModals({paymentStatus, onDismiss}: Props) {
  const {refetchSession} = useAccount();
  const queryClient = useQueryClient();
  const blocking = paymentStatus.blocking;
  // SMS-only retries always have existingOrderId → RevolutModal skips POST /api/order/
  // so the type fallback to "monthly" is never used for order creation.
  const initialType: "monthly" | "yearly" =
    paymentStatus.orderKind === "sms" ? "monthly" : paymentStatus.orderKind;

  const [type, setType] = useState<"monthly" | "yearly">(initialType);
  const [step, setStep] = useState<"initial" | "paymentData" | "fixRecurringPayment" | "payment">(
    "initial"
  );

  if (step === "initial") {
    switch (paymentStatus.reason) {
      case "endOfTrial":
        return (
          <EndOfTrialModal
            blocking={blocking}
            monthlyCentimes={paymentStatus.monthlyAmount}
            yearlyCentimes={paymentStatus.yearlyAmount}
            onRegister={(nextType) => {
              setType(nextType);
              setStep("paymentData");
            }}
            onDismiss={onDismiss}
          />
        );
      case "smsUsageFail":
        return (
          <FailedSmsPaymentModal
            blocking={blocking}
            amountCentimes={paymentStatus.amount}
            smsCount={paymentStatus.smsCount}
            smsUnitPrice={paymentStatus.smsUnitPrice}
            onFixRecurringPayment={(nextType) => {
              setType(nextType);
              setStep("fixRecurringPayment");
            }}
            onDismiss={onDismiss}
          />
        );
      case "recurringFail":
        return (
          <FailedRecurringPaymentModal
            blocking={blocking}
            paymentStatus={paymentStatus}
            onFixRecurringPayment={(nextType) => {
              setType(nextType);
              setStep("fixRecurringPayment");
            }}
            onDismiss={onDismiss}
          />
        );
      case "none":
        // Sentinel — should never reach the modal: AppModals only mounts PaymentModals
        // when subscription.paymentNeeded === true.
        return null;
      default:
        return assertNever(paymentStatus.reason);
    }
  }
  if (step === "paymentData") {
    return (
      <PaymentDataModal onAbort={() => setStep("initial")} onValidate={() => setStep("payment")} />
    );
  }
  if (step === "payment") {
    return (
      <RevolutModal
        type={type}
        existingOrderId={paymentStatus.existingOrderId}
        revolutMode={paymentStatus.mode}
        onAbort={() => setStep("paymentData")}
        onPay={handlePay}
      />
    );
  }
  if (step === "fixRecurringPayment") {
    return (
      <RevolutModal
        type={type}
        existingOrderId={paymentStatus.existingOrderId}
        revolutMode={paymentStatus.mode}
        onAbort={() => setStep("initial")}
        onPay={handlePay}
      />
    );
  }

  return null; // Invalid case

  function handlePay() {
    queryClient.invalidateQueries({queryKey: subscriptionKeysStore.root});
    // Rafraîchit l'objet company entier (dont `subscription`) via useCurrentCompany —
    // sans cette invalidation il reste stale jusqu'à 5min après paiement (#794).
    queryClient.invalidateQueries({queryKey: platformUserKeysStore.root});
    refetchSession();
  }
}
